import { FC } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar, VariantType } from "notistack";
import moment from "moment";

// API imports
import EvidenceAPI from "@/api/evidence/index";
import { getIpAddress, logActivity } from "@/api/index";

// Utils
import { db_timestamp } from "@/utils/date-format";
import { EvidenceFormData } from "../evidence.types";

// Import the base form
import DefaultEvidenceForm from "./DefaultEvidenceForm";
import { SignatureFieldRef } from "@/Features/Signatures";

interface CreateEvidenceChildFormProps {
  width?: number | undefined;
  onSubmit: (result: any) => void;
  onCancel: () => void;
  defaultInfo: any; // Parent evidence info
}

// Simplified child evidence form that extends DefaultEvidenceForm

const CreateEvidenceChildForm: FC<CreateEvidenceChildFormProps> = ({
  width,
  onSubmit,
  onCancel,
  defaultInfo,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const handleFormSubmit = async (
    formData: EvidenceFormData,
    diffData?: any,
    signatures?: {
      receivedBySig: SignatureFieldRef;
      receivedFromSig: SignatureFieldRef;
    }
  ) => {
    try {
      const processedFormData = { ...formData };
      processedFormData.case_id = defaultInfo.case_id || null;

      // Handle signatures for chain of custody if intake details are provided
      let signature: Record<string, unknown> = {};
      if (processedFormData.coc_intake_timestamp) {
        const ipAddress = await getIpAddress();

        signature.custody_to_sig =
          signatures?.receivedBySig?.isEmpty?.() === false
            ? {
                signature: signatures.receivedBySig.getSignature(),
                signer: processedFormData.custody_to?.location_name,
                timestamp: moment().toISOString(),
                userAgent: window.navigator.userAgent,
                ip_address: ipAddress,
                monolith_user: {
                  first_name: currentUser.first_name,
                  last_name: currentUser.last_name,
                  full_name: currentUser.full_name,
                  email: currentUser.email,
                  title: currentUser.title,
                  user_id: currentUser.user_id,
                },
              }
            : null;

        signature.custody_from_sig =
          signatures?.receivedFromSig?.isEmpty?.() === false
            ? {
                signature: signatures.receivedFromSig.getSignature(),
                signer: defaultInfo.evidence_number,
                timestamp: moment().toISOString(),
                userAgent: window.navigator.userAgent,
                ip_address: ipAddress,
                monolith_user: {
                  first_name: currentUser.first_name,
                  last_name: currentUser.last_name,
                  full_name: currentUser.full_name,
                  email: currentUser.email,
                  title: currentUser.title,
                  user_id: currentUser.user_id,
                },
              }
            : null;

        // Convert intake time to UTC
        processedFormData.coc_intake_timestamp = db_timestamp(
          processedFormData.coc_intake_timestamp,
          true
        );

        processedFormData.signature = JSON.stringify(signature);
        processedFormData.custody_from = defaultInfo.evidence_number || null;
        (processedFormData as any).custody_to =
          processedFormData.custody_to?.location_id;
        processedFormData.coc_type = "Intake";
        processedFormData.entered_by = currentUser.full_name;
      }

      // Process custom attributes
      const customAttributes: any[] = []; // Simple field_id/value pairs for API
      Object.keys(processedFormData).forEach((key) => {
        if (key.startsWith("custom_attribute_")) {
          customAttributes.push({
            field_id: parseInt(key.replace("custom_attribute_", "")),
            value: processedFormData[key],
          });
          delete processedFormData[key];
        }
      });

      if (customAttributes.length > 0) {
        processedFormData.custom_attributes = customAttributes;
      }

      // Create child evidence
      const result = await EvidenceAPI.createEvidence(processedFormData, {
        is_parent: 0,
        parent_id: defaultInfo.evidence_id,
        parent_name: defaultInfo.evidence_number,
      });

      enqueueSnackbar(`Created Child Evidence Item ${result.evidence_number}`, {
        variant: "success" as VariantType,
      });

      logActivity(
        result.case_id,
        currentUser.user_id,
        `Created Child Evidence Item: ${result.evidence_number}`
      );

      onSubmit(result);
    } catch (error) {
      console.error("Error creating child evidence:", error);
      enqueueSnackbar(
        "An error occurred while creating the child evidence item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <div style={{ width: width || "auto", maxWidth: "100%" }}>
      <div style={{ marginBottom: 15, fontSize: "large", fontWeight: 600 }}>
        Create Child Item
      </div>

      <DefaultEvidenceForm
        onSubmit={handleFormSubmit}
        onCancel={onCancel}
        includeCaseField={false}
        includeIntakeDetails={true}
        includeCustomFields={true}
        isChildEvidence={true}
        defaultFormData={{
          case_id: defaultInfo.case_id,
          custody_from: defaultInfo.evidence_number,
          reason: `Removed from parent ${defaultInfo.evidence_number}`,
        }}
        buttonProps={{
          cancelText: "Cancel",
          submitText: "Create Child Item",
        }}
      />
    </div>
  );
};

export default CreateEvidenceChildForm;
